import React, { useState } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components'
import {
  Desktop24Regular,
  Rocket24Regular,
  Navigation24Regular,
  Dismiss24Regular,
  Desktop20Regular,
  ClipboardTextLtr20Regular,
  Organization20Regular,
} from '@fluentui/react-icons'
import './FluentCardLanding.scss'
import { useNavigate } from 'react-router-dom'
import { createRestClient } from '@/contexts/QueryProvider'

type FluentCardLandingProps = {
  title?: string
  description?: string
  optionalText?: string
  backgroundImage?: string
  launchAppPath: string
  unityAppPath?: string
  workspaceDetailsPath: string
  appId: number
  privacyUrl?: string
  termsUrl: string
  termsAccepted: boolean
}

export const FluentCardLanding: React.FC<FluentCardLandingProps> = (props) => {
  const [termsChecked, setTermsChecked] = useState(false)
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const navigate = useNavigate()

  const { postActivityLoggingApiLogAppAccess } = createRestClient()

  const { mutate: LogAppAccess } = postActivityLoggingApiLogAppAccess.useMutation()

  const launchApp = (isTermsAccepted: boolean) => {
    LogAppAccess({ query: { appId: props.appId, popup: !isTermsAccepted } })
    navigate(props.launchAppPath)
  }

  const launchUnityApp = (isTermsAccepted: boolean) => {
    if (!props.unityAppPath) {
      return
    }
    LogAppAccess({ query: { appId: props.appId, popup: !isTermsAccepted } })
    navigate(props.unityAppPath)
  }

  const handleLaunchApplication = () => (props.termsAccepted ? launchApp(true) : setShowDialog(true))
  const handleLaunchUnityApplication = () => (props.termsAccepted ? launchUnityApp(true) : setShowDialog(true))

  const termsContent = (
    <div className="terms-content">
      <div className="check-item">
        <Checkbox
          checked={termsChecked}
          onClick={() => setTermsChecked(!termsChecked)}
          size="large"
          label={
            <>
              I have read and understood the{' '}
              <a href={props.termsUrl} target="_blank" rel="noreferrer">
                terms of use
              </a>
            </>
          }
        />
      </div>
      <div className="check-item">
        <Checkbox
          checked={privacyChecked}
          onClick={() => setPrivacyChecked(!privacyChecked)}
          size="large"
          label={
            <>
              I have read and understood the{' '}
              <a href={props.privacyUrl} target="_blank" rel="noreferrer">
                privacy statement
              </a>
            </>
          }
        />
      </div>
    </div>
  )

  const handleOpenWorkspace = () => {
    navigate(props.workspaceDetailsPath)
  }

  return (
    <div className="fluent-card-landing">
      <Dialog open={showDialog}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<Dismiss24Regular />}
                    onClick={() => setShowDialog(false)}
                  />
                </DialogTrigger>
              }
            >
              Terms of use
            </DialogTitle>
            <DialogContent>{termsContent}</DialogContent>
            <DialogActions>
              {/* <Button appearance="primary" disabled={!termsChecked || !privacyChecked} onClick={popupConfirm}> */}
              <Button appearance="primary" disabled={!termsChecked || !privacyChecked} onClick={() => launchApp(true)}>
                Confirm
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Background Image Section */}
      {props.backgroundImage && (
        <div
          className="background"
          style={{ backgroundImage: `url(${props.backgroundImage})` }}
          onClick={handleLaunchApplication}
        >
          {/* Menu Button in the bottom-right corner of the background */}
          <div className="menu-container" onClick={(e) => e.stopPropagation()}>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <button className="menu-button">
                  <Navigation24Regular />
                </button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem className="!items-center" onClick={handleLaunchApplication} icon={<Rocket24Regular />}>
                    Launch application
                  </MenuItem>
                  {props.unityAppPath && (
                    <MenuItem
                      className="!items-center"
                      onClick={handleLaunchUnityApplication}
                      icon={<Rocket24Regular />}
                    >
                      Launch legacy application (Unity)
                    </MenuItem>
                  )}
                  <MenuItem className="!items-center" onClick={handleOpenWorkspace} icon={<Desktop24Regular />}>
                    Open workspace
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="content">
        <div className="textSection">
          {props.title && (
            <div className="flex items-center gap-2">
              <ClipboardTextLtr20Regular />
              <div className="truncate font-bold" title={props.title}>
                {props.title}
              </div>
            </div>
          )}
          {props.description && (
            <div className="flex items-center gap-2">
              <Organization20Regular />
              <div className="truncate" title={props.description}>
                {props.description}
              </div>
            </div>
          )}
          {props.optionalText && (
            <div className="flex items-center gap-2">
              <Desktop20Regular />
              <div className="truncate" title={props.optionalText}>
                {props.optionalText}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
